// @ts-strict-ignore
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Radio from 'antd/lib/radio';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { antGrey } from '@/components/styles/globalStyles';
import { RootState } from '../reducers/reducers';
import { AppComponentProps } from '../typescriptHelpers';
import { selectAppSettingAutomations, selectAppStoreType } from '../selectors/selectors';
import { automationDisplayNameForAutomationId } from '../helpers/displayHelpers';
import { Automation, AutomationType } from '../types';
import { trySetAutomationPause } from '../actions/app';
import { AppUpdatable } from '../actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '../utils/globalMessage';

const LIFECYCLE_AUTOMATION_TYPES = [
    AutomationType.AutoKickoffRelease,
    AutomationType.AutoSubmitRelease,
    AutomationType.AutoRelease,
];
const LIFECYCLE_AUTOMATION_TYPE_SET = new Set<AutomationType>(LIFECYCLE_AUTOMATION_TYPES);

const isLifecycleAutomation = (automation: Automation) => LIFECYCLE_AUTOMATION_TYPE_SET.has(automation.type);

const isAutomationEnabled = (automation: Automation) =>
    automation.enabled || Object.values(automation.configPerReleaseType ?? {}).some((override) => override.enabled);

type PauseMode = 'lifecycle' | 'all' | 'selected';

interface PauseAutomationsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const PauseAutomationsModal: React.FC<PauseAutomationsModalProps> = (props: PauseAutomationsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [pendingPause, setPendingPause] = React.useState(false);
    const [pauseMode, setPauseMode] = React.useState<PauseMode>('lifecycle');

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const selectedAutomationIds: AutomationType[] = Form.useWatch('automationIds', form) ?? [];

    const labelFor = React.useCallback(
        (automation: Automation) =>
            automationDisplayNameForAutomationId(automation.type, appStoreType) ?? automation.name,
        [appStoreType],
    );

    const lifecycleAutomations = React.useMemo(() => (automations ?? []).filter(isLifecycleAutomation), [automations]);
    const enabledAutomations = React.useMemo(() => (automations ?? []).filter(isAutomationEnabled), [automations]);

    const lifecycleAutomationTypes = React.useMemo(
        () => lifecycleAutomations.map((automation) => automation.type),
        [lifecycleAutomations],
    );
    const enabledAutomationTypes = React.useMemo(
        () => enabledAutomations.map((automation) => automation.type),
        [enabledAutomations],
    );
    const enabledAutomationOptions = React.useMemo(
        () => enabledAutomations.map((automation) => ({ value: automation.type, label: labelFor(automation) })),
        [enabledAutomations, labelFor],
    );

    const isLoading = useIsLoading(AppUpdatable.SetAutomationPause, props.appId);
    const error = useError(AppUpdatable.SetAutomationPause, props.appId);
    const success = useUpdated(AppUpdatable.SetAutomationPause, props.appId);

    const tooltipList = (items: Automation[]) => (
        <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
            {items.map((automation) => (
                <li key={automation.type}>{labelFor(automation)}</li>
            ))}
        </ul>
    );

    const selectionMatchesInitial =
        selectedAutomationIds.length === enabledAutomationTypes.length &&
        selectedAutomationIds.every((automationType) => enabledAutomationTypes.includes(automationType));

    const handleReset = () => {
        form.setFieldsValue({ automationIds: enabledAutomationTypes });
    };

    React.useEffect(() => {
        if (props.open && pauseMode === 'selected') {
            form.setFieldsValue({ automationIds: enabledAutomationTypes });
        }
    }, [props.open, pauseMode, enabledAutomationTypes, form]);

    React.useEffect(() => {
        if (!props.open) {
            setPendingPause(false);
            setPauseMode('lifecycle');
        }
    }, [props.open]);

    React.useEffect(() => {
        if (success && pendingPause) {
            globalMessage.success('Automations paused!');
            setPendingPause(false);
            props.onSuccess();
        }
    }, [success, pendingPause]);

    React.useEffect(() => {
        if (error && pendingPause) {
            globalMessage.error(error);
            setPendingPause(false);
        }
    }, [error, pendingPause]);

    const dispatchPause = (automationIds: AutomationType[]) => {
        setPendingPause(true);
        dispatch(
            trySetAutomationPause(
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    enabled: false,
                    automationIds,
                },
                props.appId,
            ),
        );
    };

    const handleSubmit = () => {
        if (pauseMode === 'all') {
            if (enabledAutomationTypes.length === 0) {
                return;
            }
            dispatchPause(enabledAutomationTypes);
            return;
        }
        if (pauseMode === 'lifecycle') {
            if (lifecycleAutomationTypes.length === 0) {
                return;
            }
            dispatchPause(lifecycleAutomationTypes);
            return;
        }
        form.validateFields().then((values) => {
            dispatchPause(values.automationIds);
        });
    };

    return (
        <Modal
            title="Pause automations"
            open={props.open}
            onCancel={props.onCancel}
            onOk={handleSubmit}
            okText={
                pauseMode === 'all' ? 'Pause all automations'
                : pauseMode === 'selected' ?
                    'Pause selected automations'
                :   'Pause lifecycle automations'
            }
            confirmLoading={isLoading}
            destroyOnClose
        >
            <Text style={{ display: 'block', marginBottom: 16 }}>
                Runway will pause the selected automations for all current and upcoming releases.{' '}
                <a href="https://docs.runway.team/automations/types-of-automations" target="_blank" rel="noreferrer">
                    Learn more
                </a>
            </Text>
            <Form form={form} layout="vertical">
                <Radio.Group value={pauseMode} onChange={(e) => setPauseMode(e.target.value)} style={{ width: '100%' }}>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Radio value="lifecycle" disabled={lifecycleAutomationTypes.length === 0}>
                            <Flex align="center" gap={6}>
                                Pause lifecycle automations
                                {lifecycleAutomations.length > 0 && (
                                    <Tooltip title={tooltipList(lifecycleAutomations)}>
                                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                    </Tooltip>
                                )}
                            </Flex>
                        </Radio>
                        <Radio value="all" disabled={enabledAutomations.length === 0}>
                            Pause all automations
                        </Radio>
                        <Radio value="selected">Pause selected automations</Radio>
                    </Space>
                </Radio.Group>

                {pauseMode === 'selected' && (
                    <div style={{ marginTop: 12 }}>
                        <Flex style={{ paddingBottom: 8 }} justify="space-between" align="center">
                            <Text>Automations to pause</Text>
                            <Button
                                type="link"
                                size="small"
                                onClick={handleReset}
                                disabled={selectionMatchesInitial}
                                style={{ padding: 0, height: 'fit-content' }}
                            >
                                Reset
                            </Button>
                        </Flex>
                        <Form.Item
                            name="automationIds"
                            noStyle
                            rules={[{ required: true, message: 'Select at least one automation' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select automations"
                                options={enabledAutomationOptions}
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                            />
                        </Form.Item>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default PauseAutomationsModal;
